import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import userService from '../../services/userService';

// Types
interface GeoData {
  state_id: number | null;
  state_name: string;
  county_id: number | null;
  city_id: number | null;
  city_name: string;
  zipcode_id: number | null;
}

interface GeoState {
  states: any[];
  counties: any[];
  cities: any[];
  zipcodes: any[];
  geoData: GeoData;
  loadingStates: boolean;
  loadingCounties: boolean;
  loadingCities: boolean;
  loadingZipcodes: boolean;
  error: string | null;
}

// Initial state
const initialState: GeoState = {
  states: [],
  counties: [],
  cities: [],
  zipcodes: [],
  geoData: {
    state_id: null,
    state_name: '',
    county_id: null,
    city_id: null,
    city_name: '',
    zipcode_id: null,
  },
  loadingStates: false,
  loadingCounties: false,
  loadingCities: false,
  loadingZipcodes: false,
  error: null,
};

// Async thunks
export const loadStates = createAsyncThunk(
  'geo/loadStates',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userService.getStates();
      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to load states');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load states');
    }
  }
);

export const loadCounties = createAsyncThunk(
  'geo/loadCounties',
  async (stateId: number, { rejectWithValue }) => {
    try {
      const response = await userService.getCounties(stateId);
      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to load counties');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load counties');
    }
  }
);

export const loadCities = createAsyncThunk(
  'geo/loadCities',
  async (stateId: number, { rejectWithValue }) => {
    try {
      const response = await userService.getCities(stateId);
      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to load cities');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load cities');
    }
  }
);

export const loadZipcodes = createAsyncThunk(
  'geo/loadZipcodes',
  async ({ cityId, stateId }: { cityId: number; stateId: number }, { rejectWithValue }) => {
    try {
      const response = await userService.getZipcodes(cityId, stateId);
      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to load zipcodes');
      }
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load zipcodes');
    }
  }
);

// Slice
const geoSlice = createSlice({
  name: 'geo',
  initialState,
  reducers: {
    setGeoData: (state, action: PayloadAction<Partial<GeoData>>) => {
      state.geoData = { ...state.geoData, ...action.payload };
    },
    setStateData: (state, action: PayloadAction<{ id: number | null; name: string }>) => {
      state.geoData.state_id = action.payload.id;
      state.geoData.state_name = action.payload.name;
      // Reset cascading data
      state.geoData.county_id = null;
      state.geoData.city_id = null;
      state.geoData.city_name = '';
      state.geoData.zipcode_id = null;
      state.counties = [];
      state.cities = [];
      state.zipcodes = [];
    },
    setCountyData: (state, action: PayloadAction<number | null>) => {
      state.geoData.county_id = action.payload;
      // Reset cascading data
      state.geoData.city_id = null;
      state.geoData.city_name = '';
      state.geoData.zipcode_id = null;
      state.cities = [];
      state.zipcodes = [];
    },
    setCityData: (state, action: PayloadAction<{ id: number | null; name: string }>) => {
      state.geoData.city_id = action.payload.id;
      state.geoData.city_name = action.payload.name;
      // Reset cascading data
      state.geoData.zipcode_id = null;
      state.zipcodes = [];
    },
    setZipcodeData: (state, action: PayloadAction<number | null>) => {
      state.geoData.zipcode_id = action.payload;
    },
    resetGeoData: (state) => {
      state.geoData = {
        state_id: null,
        state_name: '',
        county_id: null,
        city_id: null,
        city_name: '',
        zipcode_id: null,
      };
      state.states = [];
      state.counties = [];
      state.cities = [];
      state.zipcodes = [];
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Load states
      .addCase(loadStates.pending, (state) => {
        state.loadingStates = true;
        state.error = null;
      })
      .addCase(loadStates.fulfilled, (state, action) => {
        state.loadingStates = false;
        state.states = action.payload;
        state.error = null;
      })
      .addCase(loadStates.rejected, (state, action) => {
        state.loadingStates = false;
        state.error = action.payload as string;
      })
      // Load counties
      .addCase(loadCounties.pending, (state) => {
        state.loadingCounties = true;
        state.error = null;
      })
      .addCase(loadCounties.fulfilled, (state, action) => {
        state.loadingCounties = false;
        state.counties = action.payload;
        state.error = null;
      })
      .addCase(loadCounties.rejected, (state, action) => {
        state.loadingCounties = false;
        state.error = action.payload as string;
      })
      // Load cities
      .addCase(loadCities.pending, (state) => {
        state.loadingCities = true;
        state.error = null;
      })
      .addCase(loadCities.fulfilled, (state, action) => {
        state.loadingCities = false;
        state.cities = action.payload;
        state.error = null;
      })
      .addCase(loadCities.rejected, (state, action) => {
        state.loadingCities = false;
        state.error = action.payload as string;
      })
      // Load zipcodes
      .addCase(loadZipcodes.pending, (state) => {
        state.loadingZipcodes = true;
        state.error = null;
      })
      .addCase(loadZipcodes.fulfilled, (state, action) => {
        state.loadingZipcodes = false;
        state.zipcodes = action.payload;
        state.error = null;
      })
      .addCase(loadZipcodes.rejected, (state, action) => {
        state.loadingZipcodes = false;
        state.error = action.payload as string;
      });
  },
});

// Export actions
export const {
  setGeoData,
  setStateData,
  setCountyData,
  setCityData,
  setZipcodeData,
  resetGeoData,
  setError,
} = geoSlice.actions;

// Export selectors
export const selectStates = (state: { geo: GeoState }) => state.geo.states;
export const selectCounties = (state: { geo: GeoState }) => state.geo.counties;
export const selectCities = (state: { geo: GeoState }) => state.geo.cities;
export const selectZipcodes = (state: { geo: GeoState }) => state.geo.zipcodes;
export const selectGeoData = (state: { geo: GeoState }) => state.geo.geoData;
export const selectGeoLoading = createSelector(
  [(state: { geo: GeoState }) => state.geo],
  (geo) => ({
    loadingStates: geo.loadingStates,
    loadingCounties: geo.loadingCounties,
    loadingCities: geo.loadingCities,
    loadingZipcodes: geo.loadingZipcodes,
  })
);

// Export reducer
export default geoSlice.reducer;

